import { Toaster } from '@/components/ui/toaster'
import { Toaster as Sonner } from '@/components/ui/sonner'
import { TooltipProvider } from '@/components/ui/tooltip'
import {
  createBrowserRouter,
  RouterProvider,
  ScrollRestoration,
  Outlet,
  Navigate,
  useParams,
} from 'react-router-dom'
import Dashboard from '@/pages/Dashboard'
import Submissions from '@/pages/Submissions'
import JoinOrganization from '@/pages/JoinOrganization'
import AcceptOrgInviteModal from '@/components/AcceptOrgInviteModal'
import Signup from '@/pages/Signup'
import EmailConfirmation from '@/pages/EmailConfirmation'
import ConfirmationSent from '@/pages/ConfirmationSent'
import AcceptInvitation from '@/pages/AcceptInvitation'
import NoOrganization from '@/pages/NoOrganization'

// ...

import Campaigns from '@/pages/Campaigns'
import CampaignCreate from '@/pages/CampaignCreate'
import CampaignDetails from '@/pages/CampaignDetails'
import CollaborationWorkspace from '@/pages/CollaborationWorkspace'
import CollaborationDetails from '@/pages/CollaborationDetails'
import CollaborationLicensing from '@/pages/CollaborationLicensing'
import Marketplace from '@/pages/Marketplace'
import AthleteProfile from '@/pages/AthleteProfile'
import Messages from '@/pages/Messages'
import Invitations from '@/pages/Invitations'
import Settings from '@/pages/Settings'
import Payments from '@/pages/Payments'
import BrandSetup from '@/pages/setup/BrandSetup'
import BrandIntake from '@/pages/setup/BrandIntake'
import ConnectTools from '@/pages/setup/ConnectTools'
import Help from '@/pages/Help'
import TermsAndConditions from '@/pages/TermsAndConditions'
import SignIn from '@/pages/SignIn'
import VerifyMagicLink from '@/pages/VerifyMagicLink'
import ForgotPassword from '@/pages/ForgotPassword'
import ResetPassword from '@/pages/ResetPassword'
import NotFound from '@/pages/NotFound'
import TalentSheet from '@/pages/TalentSheet'
import TalentSheetAthlete from '@/pages/TalentSheetAthlete'
import PublicShop from './pages/PublicShop'
import { store } from '@/store'
import { collabPath } from '@/lib/collabPath'
import { Provider } from 'react-redux'
import ProtectedRoute from '@/components/ProtectedRoute'
import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { fetchCurrentUser, clearUser } from '@/redux/userSlice'
import type { AppDispatch } from '@/store'
import { configureAuth, DemoFlow, usePageTracking } from '@adthlete/shared-frontend'
import { registerDevTools } from '@/utils/devTools'

registerDevTools()

// Configure the shared in-memory auth store: refresh endpoint origin, a per-app
// BroadcastChannel for multi-tab token sharing, and how to clear user state on session loss.
configureAuth({
  apiBaseUrl: '/api',
  channelName: 'adthlete_marketer_auth',
  appId: 'marketer',
  onAuthCleared: () => store.dispatch(clearUser()),
})

const isMockMode = import.meta.env.VITE_MOCK_STATE === 'true'

// Does not use router hooks — safe to live outside the router tree.
const AppInitializer = ({ children }: { children: React.ReactNode }) => {
  const dispatch = useDispatch<AppDispatch>()

  useEffect(() => {
    if (isMockMode) return
    dispatch(fetchCurrentUser())
  }, [dispatch])

  return <>{children}</>
}

function RootLayout() {
  // Page views, time on each screen, exits and abandoned forms, for the admin Behavior page.
  // Each campaign wizard step counts as its own screen, so drop-off shows step by step
  usePageTracking('marketer', { keepParams: ['step'] })
  return (
    <>
      <ScrollRestoration />
      <Outlet />
      <AcceptOrgInviteModal />
    </>
  )
}

const basename = (import.meta.env.VITE_BASE ?? '/').replace(/\/$/, '')

// The offer, review and live-post pages are stages of the collaboration workspace now;
// old links (notifications, bookmarks) land there, opened on what needs attention
function ToWorkspace() {
  const { campaignId, collaborationId } = useParams()
  return <Navigate to={collabPath(campaignId, collaborationId!)} replace />
}

const router = createBrowserRouter(
  [
    {
      element: <RootLayout />,
      children: [
        { path: '/signin', element: <SignIn /> },
        { path: '/signup', element: <Signup /> },
        { path: '/confirm-email', element: <EmailConfirmation /> },
        { path: '/confirmation-sent', element: <ConfirmationSent /> },
        { path: '/invitation/:token', element: <AcceptInvitation /> },
        { path: '/verify-magic-link/:token', element: <VerifyMagicLink /> },
        { path: '/forgot-password', element: <ForgotPassword /> },
        { path: '/reset-password', element: <ResetPassword /> },
        { path: '/join-organization/:token', element: <JoinOrganization /> },
        // Talent sheets managers share with brands: public, so brands can look before signing up
        { path: '/sheets/:hashedId', element: <TalentSheet /> },
        { path: '/sheets/:hashedId/athletes/:athleteId', element: <TalentSheetAthlete /> },
        // An athlete's Content Shop from a shared link: anyone can look, booking needs sign-in
        { path: '/shop/:id', element: <PublicShop /> },

        {
          element: <ProtectedRoute />,
          children: [
            { path: '/no-organization', element: <NoOrganization /> },
            { path: '/', element: <Dashboard /> },
            // Results live on the dashboard for now
            { path: '/performance', element: <Navigate to="/" replace /> },
            { path: '/campaign-performance', element: <Navigate to="/" replace /> },
            { path: '/submissions', element: <Submissions /> },
            { path: '/campaigns', element: <Campaigns /> },
            {
              path: '/campaign/create',
              element: <Navigate to="/campaign/create/step1" replace />,
            },
            { path: '/campaign/create/:step', element: <CampaignCreate /> },
            { path: '/campaign/:id', element: <CampaignDetails /> },
            {
              path: '/campaign/:campaignId/athlete/:collaborationId',
              element: <CollaborationWorkspace />,
            },
            // The previous detail page, for editing terms, due dates and files
            {
              path: '/campaign/:campaignId/athlete/:collaborationId/manage',
              element: <CollaborationDetails />,
            },
            {
              path: '/campaign/:campaignId/athlete/:collaborationId/offer',
              element: <ToWorkspace />,
            },
            {
              path: '/campaign/:campaignId/athlete/:collaborationId/collaboration-live',
              element: <ToWorkspace />,
            },
            {
              path: '/campaign/:campaignId/athlete/:collaborationId/licensing',
              element: <CollaborationLicensing />,
            },
            {
              path: '/campaign/:campaignId/athlete/:collaborationId/milestone/:milestoneId/review',
              element: <ToWorkspace />,
            },
            { path: '/campaign/:campaignId/athletes', element: <Marketplace /> },
            // The Marketplace: find athletes (Discover Athletes before)
            { path: '/marketplace', element: <Marketplace /> },
            { path: '/discover-athletes', element: <Navigate to="/marketplace" replace /> },
            { path: '/messages', element: <Messages /> },
            { path: '/messages/:id', element: <Messages /> },
            { path: '/invitations', element: <Invitations /> },
            { path: '/athlete/:id', element: <AthleteProfile /> },
            { path: '/settings', element: <Settings /> },
            { path: '/payments', element: <Payments /> },
            { path: '/manage-payments', element: <Navigate to="/payments" replace /> },
            // Brand setup: the first screen for a new organization, then connecting tools
            { path: '/welcome', element: <BrandIntake /> },
            { path: '/setup', element: <BrandSetup /> },
            { path: '/setup/connect', element: <ConnectTools /> },
            { path: '/help', element: <Help /> },
            { path: '/terms', element: <TermsAndConditions /> },
            // Open calls were replaced by the Marketplace: brands book an athlete's content
            // themes from their rate card there, or send a custom offer. Old links land there.
            { path: '/auctions/*', element: <Navigate to="/marketplace" replace /> },
            // Collaboration routes without the campaign in the URL (auction deals, and deals opened
            // from a message conversation)
            {
              path: '/collaborations/:collaborationId',
              element: <CollaborationWorkspace />,
            },
            // The previous detail page, for editing terms, due dates and files
            {
              path: '/collaborations/:collaborationId/manage',
              element: <CollaborationDetails />,
            },
            {
              path: '/collaborations/:collaborationId/offer',
              element: <ToWorkspace />,
            },
            {
              path: '/collaborations/:collaborationId/licensing',
              element: <CollaborationLicensing />,
            },
            {
              path: '/collaborations/:collaborationId/collaboration-live',
              element: <ToWorkspace />,
            },
            {
              path: '/collaborations/:collaborationId/milestone/:milestoneId/review',
              element: <ToWorkspace />,
            },
          ],
        },

        { path: '*', element: <NotFound /> },
      ],
    },
  ],
  { basename },
)

const App = () => (
  <Provider store={store}>
    <AppInitializer>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <RouterProvider router={router} />
        {/* Presenter's guide for demos (?demo=1); never on production */}
        <DemoFlow app="marketer" />
      </TooltipProvider>
    </AppInitializer>
  </Provider>
)

export default App
